import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useProfileStore } from "@/store/profile";
import { DEDICATED_REASON_ROUTES } from "@/utils/complaintSteps";
import {
  usePrimaryComplaintStore,
  VisitReason,
  ComplaintHistoryStep,
  type ComplaintHeader,
} from "@/store/primaryComplaint";
import { useTaskStore } from "@/store/task";
import { complaintLimitFor } from "@/types/task-actions";
import { FAMILY_HISTORY_PATH, wizardPrevPath } from "@/hooks/useWizardFlow";
import { finishHistoryStep } from "@/utils/navigateToFormHandler";
import { complaintHistoryLabels } from "@/components/complaints/complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { HumanBodyView } from "@/components/human-body/HumanBodyView";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { StepLoadError } from "@/components/complaints/ComplaintStepHeader";
import { cn } from "@/utils/cn";
import { confirmToast } from "@/utils/confirmToast";

const labels = {
  currentComplaints: "Your complaints",
  addComplaint: "Add a complaint",
  addAnotherComplaint: "Add another complaint",
  visitReasonTitle: "What is the primary reason for your visit today?",
  additionalComplaintTitle: "What additional complaints would you like to have addressed?",
  selectBodyArea: "Where is it?",
  changeReason: "Change reason",
  back: "Back",
  cancel: "Cancel",
  next: "Next",
  continue: "Continue",
  pain: "Pain",
  numbness: "Numbness",
  weakness: "Weakness",
  headaches: "Headaches",
  dizziness: "Dizziness",
  wellness: "Wellness",
  incomplete: "Incomplete",
  resume: "Resume",
  edit: "Edit",
  remove: "Remove",
  finishFirst: "Finish this complaint before adding another or continuing.",
  noComplaints: "Add at least one complaint to continue.",
  complaintLimitReached: "You've added as many complaints as you can here. You can tell your provider about anything else at your visit.",
  deleteComplaintConfirm: "Remove this complaint? This cannot be undone.",
  deleteFailed: "We couldn't remove that complaint. Check your connection and try again.",
} as const;

/** Visit reason cards: image (top 72%), title + subtitle (bottom bar). Images from public/. */
const VISIT_REASON_CARDS: { reason: VisitReason; image: string; title: string; subtitle: string }[] = [
  { reason: VisitReason.PAIN, image: "/pain.png", title: "Pain", subtitle: "Sharp, dull, aching, or burning" },
  { reason: VisitReason.NUMBNESS, image: "/Numbness.png", title: "Numbness", subtitle: "Tingling or loss of sensation" },
  { reason: VisitReason.WEAKNESS, image: "/weakness.png", title: "Weakness", subtitle: "Reduced strength or stamina" },
  { reason: VisitReason.HEADACHES, image: "/headaches.png", title: "Headaches", subtitle: "Head or neck-related pain" },
  { reason: VisitReason.DIZZINESS, image: "/dizziness.png", title: "Dizziness", subtitle: "Vertigo or balance issues" },
  { reason: VisitReason.WELLNESS, image: "/wellness.png", title: "Wellness", subtitle: "Maintenance & preventive care" },
];

/**
 * Region a headache complaint is tagged with. Outcome assessments are mapped per body region in
 * the clinic's admin setup (Head → its configured OAT). Storing region "Head" — the same value a
 * body-map head complaint stores (see bodyPartToRegion: face → "Head") — lets that existing mapping
 * attach the head OAT automatically, with no hardcoded OAT id. Dizziness/Wellness stay region-less.
 */
const HEAD_REGION = "Head";

const BODY_MAP_REASONS = [VisitReason.PAIN, VisitReason.NUMBNESS, VisitReason.WEAKNESS];

function complaintButtonLabel(header: ComplaintHeader): string {
  if (header.description?.trim()) return header.description.trim();
  const region = header.region?.trim() || "Unknown area";
  const type = header.complaintType?.trim();
  if (type) return `${region} – ${type}`;
  return region;
}

const isIncomplete = (h: ComplaintHeader) => h.complete === false || h.complete === undefined;

/**
 * Primary complaint: the patient's complaints with Resume / Edit / Remove, plus "Add a complaint",
 * which opens the visit-reason picker (select a card, then Next) and, for pain / numbness /
 * weakness, the body map. Nothing is created until Next.
 */
export function PrimaryComplaintPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const patientID = useProfileStore((s) => s.currentPatient?.patientID?.toString() ?? "");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBodyMap, setShowBodyMap] = useState(false);
  const [visitReason, setVisitReason] = useState<VisitReason | null>(null);
  const [region, setRegion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editingComplaintId = searchParams.get("complaintId") ?? null;

  const complaintHeaders = usePrimaryComplaintStore((s) => s.complaintHeaders);
  const currentComplaintHeader = usePrimaryComplaintStore((s) => s.currentComplaintHeader);
  const fetchComplaints = usePrimaryComplaintStore((s) => s.fetchComplaints);
  const setVisitReasonStore = usePrimaryComplaintStore((s) => s.setVisitReason);
  const setHumanBody = usePrimaryComplaintStore((s) => s.setHumanBody);
  const clearHumanBody = usePrimaryComplaintStore((s) => s.clearHumanBody);
  const clearBodyMap = usePrimaryComplaintStore((s) => s.clearBodyMap);
  const createComplaint = usePrimaryComplaintStore((s) => s.createComplaint);
  const updateComplaint = usePrimaryComplaintStore((s) => s.updateComplaint);
  const setComplaintHistoryStep = usePrimaryComplaintStore((s) => s.setComplaintHistoryStep);
  const deleteComplaint = usePrimaryComplaintStore((s) => s.deleteComplaint);
  const wizardSteps = useTaskStore((s) => s.wizardSteps);
  // The case history config caps how many complaints the patient can add (no cap without a config).
  const complaintLimit = useTaskStore((s) => complaintLimitFor(s.portalConfigMeta));

  // "loading" until the complaint fetch settles, so the list never flashes before we know whether
  // there are any; "error" when it failed, which must never read as "you have no complaints".
  const [listStatus, setListStatus] = useState<"loading" | "error" | "ready">("loading");
  const loadComplaints = useCallback(() => {
    if (!patientID) {
      setListStatus("ready");
      return () => {};
    }
    let cancelled = false;
    setListStatus("loading");
    fetchComplaints()
      .then(() => {
        if (!cancelled) setListStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setListStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [patientID, fetchComplaints]);
  useEffect(() => loadComplaints(), [loadComplaints]);

  const resetAddForm = useCallback(() => {
    setVisitReason(null);
    setRegion("");
    setShowBodyMap(false);
    setError(null);
    clearHumanBody();
    clearBodyMap();
  }, [clearHumanBody, clearBodyMap]);

  // With no complaints yet, go straight to the picker instead of an empty list.
  useEffect(() => {
    if (listStatus === "ready" && complaintHeaders !== null && complaintHeaders.length === 0) {
      setShowAddForm(true);
      resetAddForm();
    }
  }, [listStatus, complaintHeaders, resetAddForm]);

  // Arriving with ?add=1 (from the recap's "Add another complaint") opens the picker directly.
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setShowAddForm(true);
      resetAddForm();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, resetAddForm]);

  // Edit mode: came back from complaint-history step 1 (Back). Show the form prefilled.
  const hasPrefilledEditRef = useRef<string | null>(null);
  // What the edited complaint was saved as, so an unchanged Back → Next does not rewrite it.
  const editOriginalRef = useRef<{ type: string; region: string } | null>(null);
  useEffect(() => {
    if (!editingComplaintId) {
      hasPrefilledEditRef.current = null;
      return;
    }
    setShowAddForm(true);
    if (hasPrefilledEditRef.current === editingComplaintId) return;
    // After a refresh the history screens rebuild the current header from the URL with only its id
    // (type/region blank). Prefer the full entry from complaintHeaders, and wait for it to load.
    // It must also be this complaint's header: browser Back can return here with another one current.
    const usableCurrent =
      currentComplaintHeader?.complaintType && String(currentComplaintHeader.id) === editingComplaintId
        ? currentComplaintHeader
        : null;
    const header = usableCurrent ?? complaintHeaders?.find((h) => String(h.id) === editingComplaintId);
    if (!header) return;
    hasPrefilledEditRef.current = editingComplaintId;
    const type = (header.complaintType ?? "").toLowerCase() as VisitReason;
    if (Object.values(VisitReason).includes(type)) {
      setVisitReason(type);
      setShowBodyMap(BODY_MAP_REASONS.includes(type));
    }
    // region is "" (not null) when unknown, so fall back with || rather than ??.
    const regionVal = header.region || header.description || "";
    editOriginalRef.current = { type, region: regionVal };
    // Drop a body-map list left over from another complaint so the map doesn't offer its sub-areas.
    clearBodyMap();
    if (regionVal) setRegion(regionVal);
    setHumanBody({
      selectedPart: { partName: "", side: "", label: regionVal },
      selectedInternalPart: regionVal,
    });
  }, [editingComplaintId, currentComplaintHeader, complaintHeaders, setHumanBody, clearBodyMap]);

  const hasComplaints = (complaintHeaders?.length ?? 0) > 0;
  const atComplaintLimit = (complaintHeaders?.length ?? 0) >= complaintLimit;

  // At the cap, a new-complaint picker (e.g. opened by ?add=1 before the list loaded) closes again.
  // Editing an existing complaint is still allowed.
  useEffect(() => {
    if (listStatus !== "ready" || !atComplaintLimit || !showAddForm || editingComplaintId) return;
    setShowAddForm(false);
    resetAddForm();
  }, [listStatus, atComplaintLimit, showAddForm, editingComplaintId, resetAddForm]);
  const incompleteHeader = complaintHeaders?.find(isIncomplete) ?? null;

  // Forward path into this step is Financial (Q1) → Insurance (Q2) → here, so Back returns to the
  // insurance question (Q2), not two steps back to Q1.
  const clearRegion = useCallback(() => setRegion(""), []);

  // When the General History config puts complaints last (complaintsFirst: false), Back returns to
  // Family History, the step before this one.
  const goBack = () =>
    navigate(
      wizardPrevPath(wizardSteps, "/complaint/primary-complaint") === FAMILY_HISTORY_PATH
        ? FAMILY_HISTORY_PATH
        : "/complaint/financial-responsibility-insurance"
    );

  const closeAddForm = () => {
    setShowAddForm(false);
    resetAddForm();
    if (editingComplaintId) setSearchParams({});
  };

  const goToComplaintHistory = (complaintId: string | number) => {
    navigate(`/complaint/complaint-history?complaintId=${complaintId}&patientID=${patientID}`);
  };

  const handleOpenExisting = (header: ComplaintHeader) => {
    usePrimaryComplaintStore.setState({ currentComplaintHeader: header });
    setComplaintHistoryStep(ComplaintHistoryStep.ComplaintHistory1);
    const type = (header.complaintType ?? "").toLowerCase() as VisitReason;
    if (Object.values(VisitReason).includes(type)) setVisitReasonStore(type);
    const regionLabel = header.region ?? "";
    if (regionLabel) {
      setHumanBody({
        selectedPart: { partName: "", side: "", label: regionLabel },
        selectedInternalPart: regionLabel,
      });
    }
    if (header.id == null || !patientID) return;
    const dedicated = DEDICATED_REASON_ROUTES[type];
    if (dedicated) {
      navigate(`${dedicated}?complaintId=${header.id}&patientID=${patientID}`);
      return;
    }
    goToComplaintHistory(header.id);
  };

  const handleAddNewClick = () => {
    if (atComplaintLimit) return;
    setSearchParams({});
    setShowAddForm(true);
    resetAddForm();
    // Start the next complaint clean at step 1 — otherwise it inherits the previous complaint's
    // step/answers and skips its own onset questions.
    setComplaintHistoryStep(ComplaintHistoryStep.ComplaintHistory1);
    usePrimaryComplaintStore.setState({ currentComplaintValues: null, currentComplaintHeader: null });
  };

  /** Create a headache / dizziness / wellness complaint and open its dedicated screen. */
  const createDedicated = async (reason: VisitReason, route: string) => {
    setVisitReasonStore(reason);
    clearBodyMap();
    clearHumanBody();
    if (reason === VisitReason.HEADACHES) {
      // Tag with the Head region so the clinic's region→OAT mapping attaches the head outcome
      // assessment to this headache complaint automatically (no hardcoded OAT id).
      setHumanBody({
        selectedPart: { partName: "", side: "", label: HEAD_REGION },
        selectedInternalPart: "",
      });
    }
    const reasonTitle = VISIT_REASON_CARDS.find((c) => c.reason === reason)?.title ?? reason;
    await createComplaint(patientID || undefined, reasonTitle);
    const id = usePrimaryComplaintStore.getState().currentComplaintHeader?.id;
    if (id == null) throw new Error("no complaint id");
    navigate(`${route}?complaintId=${id}&patientID=${patientID}`);
  };

  /** Create (or update, when editing) a body-map complaint and open complaint history. */
  const createWithRegion = async (reason: VisitReason) => {
    setVisitReasonStore(reason);
    if (editingComplaintId) {
      // Unchanged reason and area: keep the stored problem name (e.g. "Pain in the Left Wrist")
      // instead of rebuilding it from the coarser region.
      const original = editOriginalRef.current;
      const unchanged = original && original.type === reason && original.region === region.trim();
      if (!unchanged) await updateComplaint(editingComplaintId, patientID);
      clearBodyMap();
      setSearchParams({});
      goToComplaintHistory(editingComplaintId);
      return;
    }
    await createComplaint();
    clearBodyMap();
    const id = usePrimaryComplaintStore.getState().currentComplaintHeader?.id;
    if (id == null) throw new Error("no complaint id");
    goToComplaintHistory(id);
  };

  const handleNext = async () => {
    if (!visitReason) return;
    if (!patientID) {
      setError("Please try again in a moment.");
      return;
    }
    // Pain / numbness / weakness: the first Next moves on to the body map.
    if (BODY_MAP_REASONS.includes(visitReason) && !showBodyMap) {
      setShowBodyMap(true);
      return;
    }
    if (showBodyMap && !region.trim()) return;
    if (!editingComplaintId && atComplaintLimit) {
      setError(labels.complaintLimitReached);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const dedicated = DEDICATED_REASON_ROUTES[visitReason];
      if (dedicated) await createDedicated(visitReason, dedicated);
      else await createWithRegion(visitReason);
    } catch (e: unknown) {
      console.error(e);
      const message =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: string | { message?: string } } }).response?.data
          : null;
      setError(
        typeof message === "string"
          ? message
          : message && typeof message === "object" && typeof message.message === "string"
            ? message.message
            : "We couldn't start this complaint. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComplaint = async (header: ComplaintHeader) => {
    if (header.id == null) return;
    const confirmed = await confirmToast(labels.deleteComplaintConfirm, {
      confirmLabel: labels.remove,
      cancelLabel: labels.cancel,
      variant: "destructive",
    });
    if (!confirmed) return;
    await removeComplaint(header.id);
  };

  /** Delete an already-confirmed complaint; a failure offers Try again without re-confirming. */
  const removeComplaint = async (id: string | number) => {
    try {
      await deleteComplaint(id);
    } catch (err) {
      console.error(err);
      toast.error(labels.deleteFailed, {
        id: "complaint-delete-error",
        action: { label: "Try again", onClick: () => void removeComplaint(id) },
      });
    }
  };

  const handleContinue = () => {
    if (!hasComplaints || incompleteHeader) return;
    finishHistoryStep(
      navigate,
      wizardSteps,
      "/complaint/primary-complaint",
      // No next history step → finish; never fall back to the hardcoded health-history pages.
      "/complaint/task-complete"
    ).catch(() => toast.error(complaintHistoryLabels.finishFailed, { id: "history-finish-error" }));
  };

  const reasonTitle = visitReason ? labels[visitReason] : "";

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content flex flex-col px-4 pt-8 pb-4">
        <div className="mx-auto my-auto w-full max-w-2xl">
          {listStatus === "loading" && !showAddForm ? (
            <div className="wizard-skeleton" role="status" aria-busy="true" aria-label="Loading your complaints">
              <span />
              <span />
              <span />
            </div>
          ) : listStatus === "error" && !showAddForm ? (
            <StepLoadError onRetry={loadComplaints} />
          ) : !showAddForm ? (
            <section className="complaint-list" aria-labelledby="complaint-list-heading">
              <h2 id="complaint-list-heading" className="complaint-list__heading">
                {labels.currentComplaints}
              </h2>

              {hasComplaints ? (
                <ul className="complaint-list__rows">
                  {complaintHeaders!.map((h) => {
                    const incomplete = isIncomplete(h);
                    return (
                      <li key={String(h.id ?? h.region)} className="complaint-row">
                        <div className="complaint-row__main">
                          <span className="complaint-row__name">{complaintButtonLabel(h)}</span>
                          {incomplete && <span className="complaint-row__badge">{labels.incomplete}</span>}
                          {incomplete && <p className="complaint-row__note">{labels.finishFirst}</p>}
                        </div>
                        <div className="complaint-row__actions">
                          <Button
                            type="button"
                            variant={incomplete ? "primary" : "outline"}
                            onClick={() => handleOpenExisting(h)}
                          >
                            {incomplete ? labels.resume : labels.edit}
                          </Button>
                          <button
                            type="button"
                            className="complaint-row__remove"
                            onClick={() => void handleDeleteComplaint(h)}
                            aria-label={`${labels.remove} ${complaintButtonLabel(h)}`}
                          >
                            {labels.remove}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="complaint-list__empty">{labels.noComplaints}</p>
              )}

              {atComplaintLimit ? (
                <p className="complaint-list__empty">{labels.complaintLimitReached}</p>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="complaint-list__add"
                  onClick={handleAddNewClick}
                  disabled={!!incompleteHeader}
                >
                  + {hasComplaints ? labels.addAnotherComplaint : labels.addComplaint}
                </Button>
              )}
            </section>
          ) : !showBodyMap ? (
            <div className="mx-auto max-w-6xl">
              <h2 className="complaint-list__heading">
                {hasComplaints ? labels.additionalComplaintTitle : labels.visitReasonTitle}
              </h2>
              <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:gap-6">
                {VISIT_REASON_CARDS.map(({ reason, image, title, subtitle }) => {
                  const selected = visitReason === reason;
                  return (
                    <button
                      key={reason}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setVisitReason(reason);
                        setError(null);
                      }}
                      disabled={submitting}
                      className={cn("reason-tile", selected && "reason-tile--selected")}
                    >
                      <div className="relative h-[72%] min-h-0 flex-shrink-0 overflow-hidden">
                        <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />
                        {selected && <i className="fas fa-check reason-tile__check" aria-hidden />}
                      </div>
                      <div className="flex h-[28%] min-h-0 flex-shrink-0 flex-col justify-center bg-healthsource-dark px-2 py-px">
                        <p className="font-bold text-[15px] leading-none text-white">{title}</p>
                        <p className="mt-px truncate text-[12px] font-normal leading-none text-slate-200">{subtitle}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="complaint-list__heading mb-0">
                  {region ? `${reasonTitle} in the ${region}` : labels.selectBodyArea}
                </h2>
                {!editingComplaintId && (
                  <button type="button" className="text-link" onClick={() => setShowBodyMap(false)}>
                    {labels.changeReason}
                  </button>
                )}
              </div>
              <div className="mb-4">
                <HumanBodyView
                  selectedRegion={region || undefined}
                  onSelect={setRegion}
                  onClear={clearRegion}
                  className="my-2"
                />
              </div>
            </>
          )}

          {error && (
            <p className="mb-3 text-[13px] text-healthsource-error" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>

      <WizardActionsBar>
        {!showAddForm ? (
          <>
            <Button type="button" variant="outline" onClick={goBack}>
              ← {labels.back}
            </Button>
            <Button
              type="button"
              onClick={handleContinue}
              disabled={listStatus !== "ready" || !hasComplaints || !!incompleteHeader}
            >
              {labels.continue}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={showBodyMap && !editingComplaintId ? () => setShowBodyMap(false) : hasComplaints ? closeAddForm : goBack}
            >
              {hasComplaints && !showBodyMap ? labels.cancel : `← ${labels.back}`}
            </Button>
            <Button
              type="button"
              onClick={() => void handleNext()}
              disabled={!visitReason || submitting || (showBodyMap && !region.trim())}
            >
              {submitting ? "Creating…" : labels.next}
            </Button>
          </>
        )}
      </WizardActionsBar>
    </div>
  );
}
